import React, {useEffect, useState} from 'react';
import Table from 'shared/migration-components/table';
import {useFieldArray, useForm, useFormContext} from 'react-hook-form';
import CircleButton from 'shared/migration-components/circle-button';
import {IoTrash} from 'react-icons/io5';
import Modal from 'shared/migration-components/modal';
import Form, {
  BaseInputProps,
  InputProps,
} from 'shared/migration-components/form';
import {TranslationKeyProps} from 'shared/util/translated-props';
import {NestedKeys} from 'shared/types/key-manipulation';
import {ProjectionOptionProps} from 'shared/migration-components/api-table';
import ObjectId from 'bson-objectid';

export interface DynamicTableProps<T = any>
  extends Omit<
    BaseInputProps<T>,
    | 'name'
    | 'label'
    | 'border'
    | 'translationKey'
    | 'disabled'
    | 'hidden'
    | 'information'
    | 'placeholder'
  > {
  name: string;
  inputs: InputProps<T>[];
  translationKey: TranslationKeyProps;
  createTitle: string;
  updateTitle: string;
  columns: (NestedKeys<T> | ProjectionOptionProps<T>)[];
  isShowingCreate: boolean;
  showCreate: (data: boolean) => void;
  defaultValues?: Partial<T>;
  handleSubmit?: (data: T) => T | false;
  onFormStateChange?: (data: T) => void;
}

const DynamicTable: <T>(p: DynamicTableProps<T>) => React.ReactElement<T> = ({
  name,
  inputs,
  translationKey,
  createTitle,
  updateTitle,
  columns,
  isShowingCreate,
  showCreate,
  defaultValues,
  handleSubmit,
  onFormStateChange,
}) => {
  const [updateIndex, setUpdateIndex] = useState<number>();

  const {control} = useFormContext();
  const {fields, append, remove, update} = useFieldArray({
    control,
    name,
  });

  const internalFormContext = useForm<any>({
    defaultValues,
  });

  useEffect(() => {
    if (!isNaN(Number(updateIndex)))
      internalFormContext.reset(fields[updateIndex!]);
  }, [updateIndex]);

  const handleOnClose = () => {
    showCreate(false);
    setUpdateIndex(undefined);

    internalFormContext.reset(defaultValues);
  };

  return (
    <>
      <Container>
        <Table
          data={fields as any[]}
          options={[
            ...(columns as any),
            {
              value: 'id',
              label: 'Ações',
              column: (row, rowIndex) => (
                <CircleButton
                  icon={<IoTrash />}
                  onClick={() => remove(rowIndex)}
                />
              ),
            },
          ]}
          translatedPropsKey={translationKey}
          onRowClick={(_, index) => setUpdateIndex(index)}
        />
      </Container>
      {isShowingCreate && (
        <Modal
          {...{
            opened: true,
            onClose: handleOnClose,
            title: createTitle,
          }}>
          <Form
            formContext={internalFormContext}
            preMutateCheck={(data) => {
              const isError = validateFormInputs(
                inputs,
                internalFormContext,
                translationKey,
              );

              if (isError) return;

              const createData = handleSubmit ? handleSubmit(data) : data;

              if (!createData) return;

              append({
                ...createData,
                id: String(ObjectId()),
              } as any);
              handleOnClose();
              return;
            }}
            onSubmit={() => ({} as any)}
            {...{translationKey, inputs, onFormStateChange}}
            border
          />
        </Modal>
      )}
      {!isNaN(Number(updateIndex)) && (
        <Modal
          {...{
            opened: true,
            onClose: handleOnClose,
            title: updateTitle,
          }}>
          <Form
            border
            formContext={internalFormContext}
            preMutateCheck={(data) => {
              const isError = validateFormInputs(
                inputs,
                internalFormContext,
                translationKey,
              );

              if (isError) return;

              const updateData = handleSubmit ? handleSubmit(data) : data;

              if (!updateData) return;

              update(updateIndex!, updateData);
              handleOnClose();
              return;
            }}
            onSubmit={() => ({} as any)}
            {...{translationKey, inputs, onFormStateChange}}
          />
        </Modal>
      )}
    </>
  );
};

export default DynamicTable;

import styled from 'styled-components';
import validateFormInputs from '../util/validate-form-inputs';

const Container = styled.div`
  grid-column: span 12;

  display: flex;
  flex-direction: column;
  gap: 15px;
`;
